import Vue from 'vue';

const GUEST_TYPE = ['adult', 'child', 'infant'];

function generateRemainingGuestFilter(guestFilter, cartRooms){
  let remainingGuestFilter = Vue.prototype.$deepCopy(guestFilter)
  cartRooms.forEach(savedRoomRate => {
    savedRoomRate.guest.forEach(savedRoomRateGuest => {
      const { included, extra } = savedRoomRateGuest;

      const savedGuestsPerRoomRate = GUEST_TYPE.reduce((acc, key) => {
        acc[key] = included[key] + extra[key];
        return acc;
      }, {});
      GUEST_TYPE.forEach(guest => {
        if(remainingGuestFilter[guest] > 0){
          remainingGuestFilter[guest] -= savedGuestsPerRoomRate[guest]
        } else {
          remainingGuestFilter[guest] = 0
        }
      })
    });
  })
  return remainingGuestFilter
}

function includeGuestParamsIfZeroTotal(guest_included, roomConfig, guestFilter){
  let guestIncluded = { adult: 0, child: 0, infant: 0 }
  const currentGuestFilter = Vue.prototype.$deepCopy(guestFilter)

  const { use_max_guest_by_pax, max_guest_adult, max_guest_children, max_guest_infant } = roomConfig

  if(use_max_guest_by_pax){
    guestIncluded.adult = Math.min(guest_included, max_guest_adult);

    let remainingPax = guest_included - guestIncluded.adult;

    if (currentGuestFilter.child > 0 && remainingPax > 0) {
      guestIncluded.child = Math.min(remainingPax, max_guest_children);
      remainingPax -= guestIncluded.child;
    }

    if (currentGuestFilter.infant > 0 && remainingPax > 0) {
      guestIncluded.infant = Math.min(remainingPax, max_guest_infant);
    }
  }else{
    if(currentGuestFilter.adult > guest_included){
      guestIncluded = { adult: guest_included, child: 0, infant: 0 }
    }else{
      guestIncluded.adult = currentGuestFilter.adult
      const remainingGuestIncludedAfterAdult = guest_included - currentGuestFilter.adult

      if(remainingGuestIncludedAfterAdult > 0){

        if(currentGuestFilter.child > remainingGuestIncludedAfterAdult){
          guestIncluded.child = remainingGuestIncludedAfterAdult
        }else{
          guestIncluded.child = currentGuestFilter.child
          const { adult, child } = guestIncluded
          const remainingGuestIncludedAfterAdultChild = guest_included - (adult + child)
          const isInfantExist = currentGuestFilter.infant > remainingGuestIncludedAfterAdultChild
          if(remainingGuestIncludedAfterAdultChild > 0 && isInfantExist){
            guestIncluded.infant = remainingGuestIncludedAfterAdultChild
          }
        }
      }
    }
  }
  return guestIncluded;
}

function allocateGuestPerRoom(guestParamsIncluded, remainingGuestFilter, guest_included){
  let includedGuest = guestParamsIncluded
  let remainingGuest = remainingGuestFilter

  GUEST_TYPE.forEach(guest => {
    let totalGuestIncludedPerRoom = Object.values(includedGuest).reduce((sum, value) => sum + value, 0);
    if(remainingGuest[guest] > 0 && totalGuestIncludedPerRoom < guest_included){
      let remainingPaxPerRoom = guest_included - totalGuestIncludedPerRoom
      if(remainingGuest[guest] > guest_included){
        includedGuest[guest] = remainingPaxPerRoom
        remainingGuest[guest] -= remainingPaxPerRoom
      }else{
        includedGuest[guest] = remainingPaxPerRoom > remainingGuest[guest] ? remainingGuest[guest] : remainingPaxPerRoom
        remainingGuest[guest] -= includedGuest[guest]
      }
    }
  });
  return { includedGuest, remainingGuest }
}

function allocateSpecificGuestPerRoom(remainingGuests, maxGuests, includedGuests){
  let allocatedGuests = { adult: 0, child: 0, infant: 0 };
  let remainingIncluded = includedGuests;

  const allocate = (type) => {
    if (remainingIncluded <= 0) return;
    const count = Math.min(remainingGuests[type], maxGuests[type], remainingIncluded);
    allocatedGuests[type] = count;
    remainingGuests[type] -= count;
    remainingIncluded -= count;
  }

  allocate('adult');
  allocate('child');
  allocate('infant');

  return allocatedGuests;
}

/**
 * Distributes the guest filter across the booked room quantity, splitting each
 * room into included guests and chargeable extra guests.
 * @param {number} selectedRoomRateQty total rooms being booked for this rate
 * @param {Object} currentRoomRate room rate containing rate & room_type guest limits
 * @param {Object} guestFilter current search filter guests ({ adult, child, infant })
 * @param {Array} cartRooms room rates already saved in the cart
 * @returns {Array} guest params ({ included, extra }) per booked room
 */
export function generateRoomRateGuestParam(selectedRoomRateQty, currentRoomRate, guestFilter, cartRooms){
  let finalCountGuests = []

  const { rate, room_type } = currentRoomRate
  const {
    use_max_guest_by_pax = false,
    max_guests = null,
    max_guest_adult = 0,
    max_guest_children = 0,
    max_guest_infant = 0,
  } = rate.max_guests ? rate : room_type;
  const roomConfig = {use_max_guest_by_pax, max_guests, max_guest_adult, max_guest_children, max_guest_infant}

  let remainingGuestFilter = generateRemainingGuestFilter(guestFilter, cartRooms)
  const totalRemainingGuestFilter = Object.values(remainingGuestFilter).reduce((sum, value) => sum + value, 0)

  for (let i = 0; i < selectedRoomRateQty; i++) {
    let guestParams = {
      included: { adult: 0, child: 0, infant: 0 },
      extra: { adult: 0, child: 0, infant: 0 },
    }

    if(totalRemainingGuestFilter === 0){
      guestParams.included = includeGuestParamsIfZeroTotal(rate.guest_included, roomConfig, guestFilter)
      finalCountGuests.push(guestParams);
    }else{

      if(use_max_guest_by_pax){
        const maxGuests = { adult: max_guest_adult, child: max_guest_children, infant: max_guest_infant };
        guestParams.included = allocateSpecificGuestPerRoom(remainingGuestFilter, maxGuests, rate.guest_included);
      }else{
        if(totalRemainingGuestFilter > rate.guest_included){
          const { includedGuest, remainingGuest } = allocateGuestPerRoom(guestParams.included, remainingGuestFilter, rate.guest_included)
          guestParams.included = includedGuest
          remainingGuestFilter = remainingGuest
        }else{
          guestParams.included = remainingGuestFilter
          remainingGuestFilter = { adult: 0, child: 0, infant: 0 }
        }
      }

      finalCountGuests.push(guestParams);

      const isLastLooper = i+1 === selectedRoomRateQty;
      if(isLastLooper){
        let notIncludedGuest = Object.values(remainingGuestFilter).reduce((sum, value) => sum + value, 0)
        if(notIncludedGuest > 0){
          for(let j = 0; j < selectedRoomRateQty; j++){
            let guestPerQtyRoom = finalCountGuests[j]
            if(use_max_guest_by_pax){
              const maxGuestsPerType = { adult: max_guest_adult, child: max_guest_children, infant: max_guest_infant };
              const guestIncluded = guestPerQtyRoom.included

              for (const type of GUEST_TYPE){
                let remainingPaxForExtra = maxGuestsPerType[type] - guestIncluded[type];
                if(remainingPaxForExtra <= 0) continue;

                const totalCurrentGuest = Object.values(finalCountGuests[j]).flatMap(Object.values).reduce((a, b) => a + b, 0);
                if(max_guests <= totalCurrentGuest) continue;

                const remainingFromMaxGuests = max_guests - totalCurrentGuest;
                const extraFinal = Math.min(remainingPaxForExtra, remainingGuestFilter[type], remainingFromMaxGuests);
                finalCountGuests[j].extra[type] = extraFinal;
                remainingGuestFilter[type] -= extraFinal;
              }

            }else{
              let totalGuestIncludedPerRoom = Object.values(guestPerQtyRoom.included).reduce((sum, value) => sum + value, 0);
              let remainingPaxForExtra = max_guests-totalGuestIncludedPerRoom

              for (const type of GUEST_TYPE) {
                if (remainingPaxForExtra > 0 && remainingGuestFilter[type] > 0) {
                  const extraFinal = Math.min(remainingPaxForExtra, remainingGuestFilter[type]);
                  finalCountGuests[j].extra[type] = extraFinal;
                  remainingGuestFilter[type] -= extraFinal;
                  remainingPaxForExtra -= extraFinal
                }

              }
            }
          }
        }
      }
    }
  }

  return finalCountGuests;
}



// WEBPACK FOOTER //
// ./src/components/hotel/be/v3/room-card/guestAllocation.js